import React, { useState, useCallback } from "react";
import { RouteComponentProps } from "react-router";

import { TranslationsIcon, DictionaryIcon, InfoIcon, Tree4Icon } from "@plex/icons";
import { SideMenu } from "@plex/react-components";

import { useLocalizedValues } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { DictionaryLinks } from "./dictionary-links";
import { MessageId } from "./message-id";
import { MessageRelationships } from "./message-relationships";
import { MessageTranslations } from "./message-translations";
import styles from "./MessageDetails.module.scss";

enum MessageFormTabs {
  messageFormat = "messageFormatTab",
  messageRelationships = "relationshipsTab",
  dictionaryLinks = "dictionaryLinksTab",
  translations = "translationsTab"
}

export const MessageDetails: React.FC<RouteComponentProps> = route => {
  const messageId = route.match.params["messageId"];

  const localizedValues = useLocalizedValues(() => ({
    messageIdHeader: displayMessages.title("messageId"),
    parentChildHeader: displayMessages.title("messageRelationships"),
    dictionaryLinksHeader: displayMessages.title("dictionaryLinks"),
    translationsHeader: displayMessages.title("messageTranslations")
  }));

  const [activeTab, setActiveTab] = useState<MessageFormTabs>(MessageFormTabs.messageFormat);

  const onSidebarItemClick = useCallback((tab: MessageFormTabs) => {
    setActiveTab(tab);
  }, []);

  const renderFormContent = () => {
    switch (activeTab) {
      case MessageFormTabs.messageFormat:
        return <MessageId messageIdParameter={messageId}></MessageId>;
      case MessageFormTabs.messageRelationships:
        return <MessageRelationships messageId={messageId}></MessageRelationships>;
      case MessageFormTabs.dictionaryLinks:
        return <DictionaryLinks messageId={messageId}></DictionaryLinks>;
      case MessageFormTabs.translations:
        return <MessageTranslations messageId={messageId}></MessageTranslations>;
      default:
        return <MessageId messageIdParameter={messageId}></MessageId>;
    }
  };

  const renderFormInUpdateMode = (
    <div className={activeTab === MessageFormTabs.translations ? styles.messageFormContainerWithoutFooter : styles.messageFormContainer}>
      <div className={styles.sidebar}>
        <SideMenu id="messageFormSideBar" scrolling={false} dragDrop={false}>
          <SideMenu.Item
            id={MessageFormTabs.messageFormat}
            selected
            title={localizedValues.messageIdHeader}
            text={localizedValues.messageIdHeader}
            icon={<InfoIcon />}
            onClick={() => onSidebarItemClick(MessageFormTabs.messageFormat)}
          />
          <SideMenu.Item
            id={MessageFormTabs.messageRelationships}
            title={localizedValues.parentChildHeader}
            text={localizedValues.parentChildHeader}
            icon={<Tree4Icon />}
            onClick={() => onSidebarItemClick(MessageFormTabs.messageRelationships)}
          />
          <SideMenu.Item
            id={MessageFormTabs.dictionaryLinks}
            title={localizedValues.dictionaryLinksHeader}
            text={localizedValues.dictionaryLinksHeader}
            icon={<DictionaryIcon />}
            onClick={() => onSidebarItemClick(MessageFormTabs.dictionaryLinks)}
          />
          <SideMenu.Item
            id={MessageFormTabs.translations}
            title={localizedValues.translationsHeader}
            text={localizedValues.translationsHeader}
            icon={<TranslationsIcon />}
            onClick={() => onSidebarItemClick(MessageFormTabs.translations)}
          />
        </SideMenu>
      </div>
      <div className={styles.messageFormContent}>{renderFormContent()}</div>
    </div>
  );

  const renderFormInAddMode = (
    <div className={styles.formContainerHeight}>
      <MessageId messageIdParameter={messageId}></MessageId>
    </div>
  );

  return messageId ? renderFormInUpdateMode : renderFormInAddMode;
};
